// Fetch the first wines' reveal assets while the room is still joining.
//
// Two things happen when a phone in the venue pulls a film early, and both
// matter (TAS-400):
//
//   1. That phone has it. The reveal plays off the disk instead of the network,
//      which is the difference 4458 was made of.
//   2. Everyone else's phone has it too, almost. Supabase storage sits behind
//      Cloudflare and the films were coming from the Cape Town edge — but they
//      expire there, so the first table of the evening pulls 2.7MB back from
//      Frankfurt with the room watching. One request from inside the venue
//      warms that edge for every other phone on the same network.
//
// The lobby is the right moment: nobody is waiting on anything, and the game
// cannot start until people have joined anyway.
//
// It follows the connection tier for the same reason the reveal does: on a
// connection that cannot carry a film, downloading two of them in the lobby is
// how you arrive at wine one with nothing loaded and no time left.

import { supabase } from '@tasted/utils/supabase';
import { netTier, NET_FULL, NET_SLOW, recordAssetSample, reprobeIfBelowFull } from '@tasted/utils/netTier';
import { bottleModelFor } from './bottleModels';

// Only ever the opening wines. By wine three the game has been running long
// enough for the ordinary per-step prefetch to have done its work.
const WINES_TO_WARM = 2;

let warmedFor = null; // session code, so a re-render or a rejoin does not re-pull

/**
 * @param {string} sessionCode
 * @param {string[]} wineIds  in play order
 */
export async function warmRevealAssets(sessionCode, wineIds) {
  if (!sessionCode || !Array.isArray(wineIds) || wineIds.length === 0) return;
  if (warmedFor === sessionCode) return;
  warmedFor = sessionCode;

  const tier = netTier();
  const opening = wineIds.slice(0, WINES_TO_WARM);

  const { data, error } = await supabase
    .from('wines_v2')
    .select('id, reveal_video_url, reveal_image_url, image_url')
    .in('id', opening);
  if (error || !data) return;

  // Play order, not the order the database happened to return them in: wine one
  // is the one the room reaches first and the only one guaranteed to be needed.
  const rows = opening.map((id) => data.find((w) => w.id === id)).filter(Boolean);

  for (const [index, wine] of rows.entries()) {
    // The photograph first, always. It is tens of kilobytes, it is what a slow
    // connection reveals with, and it is the poster behind every film.
    await warm(wine.reveal_image_url || wine.image_url);

    if (tier === NET_SLOW) continue;          // the photo is the reveal here
    if (index > 0 && tier !== NET_FULL) continue; // one film is enough to carry

    await warm(wine.reveal_video_url);
    if (tier === NET_FULL) await warm(bottleModelFor(wine.id));
  }
}

/**
 * One asset, read to completion and thrown away — the point is the copy the
 * browser keeps and the copy the edge keeps, not the bytes themselves.
 */
async function warm(url) {
  if (!url) return;
  try {
    const startedAt = performance.now();
    const response = await fetch(url, { credentials: 'omit' });
    if (!response.ok) return;
    const body = await response.blob();
    // Free measurement: this is a real download of a real asset over the real
    // path, which is exactly what the tier wants and cannot get from resource
    // timing (storage sends no Timing-Allow-Origin).
    recordAssetSample({ bytes: body.size, ms: performance.now() - startedAt });
  } catch {
    // A failed warm-up costs nothing — the reveal will fetch it itself.
  }
}

const warmedBottles = new Set();

/**
 * Pull this wine's 3D bottle into the browser cache as soon as the wine
 * starts, so the reveal (minutes later) builds it from the disk instead of
 * racing a 1.5–7 MB download with the room watching (Mel, 2026-09-27: "the 3D
 * model should load more greedily"). Only where the reveal would use it
 * (bottleModelFor says no on lite devices and short of a full connection),
 * and once per wine per page.
 */
export function warmBottleFor(wineId) {
  const go = () => {
    const url = wineId ? bottleModelFor(wineId) : null;
    if (!url || warmedBottles.has(url)) return;
    warmedBottles.add(url);
    void warm(url);
  };
  // A connection the game gave up on gets measured again here, at the start
  // of each wine, so a dip costs one reveal rather than the night. The warm
  // waits for that reading (Codex): asking the gate before it is in would
  // only hear the old answer.
  const remeasure = reprobeIfBelowFull();
  if (remeasure) remeasure.then(go, go);
  else go();
}

/** Test hook. */
export function __resetWarmedForTests() {
  warmedFor = null;
  warmedBottles.clear();
}
